/**
 * Document AI Insights Panel
 * ===========================
 * Comprehensive view of all Document AI processing results.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Sparkles, Brain, FileText, Languages, Workflow, 
  RefreshCw, Loader2, CheckCircle, XCircle, Clock,
  ChevronRight, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import HandwrittenPreviewPanel from './HandwrittenPreviewPanel';
import ExtractedFieldsPanel from './ExtractedFieldsPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Safe getter for nested properties - avoids undefined errors
 */
const safeGet = (obj, ...keys) => {
  let result = obj;
  for (const key of keys) {
    if (result == null || typeof result !== 'object') return undefined;
    result = result[key];
  }
  return result;
};

/**
 * Safe display value - returns "-" for null/undefined
 */
const safeDisplay = (value, defaultValue = '—') => {
  if (value === null || value === undefined || value === '') return defaultValue;
  return value;
};

const DocumentAIInsights = ({ 
  documentId, 
  metadata, 
  documentType,
  headers,
  onRefresh 
}) => {
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [aiStatus, setAiStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Safe access to AI data with fallbacks
  const visionAI = metadata?.__vision_ai || {};
  const aiInsight = metadata?.__ai_insight || {};
  const aiProcessedAt = metadata?.__ai_processed_at || aiInsight?.processed_at;

  useEffect(() => {
    loadAIStatus();
  }, []);

  const loadAIStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/document-ai/status`, {
        headers
      });
      if (response.ok) {
        const data = await response.json();
        setAiStatus(data);
      }
    } catch (err) {
      console.log('Document AI status not available');
    }
  };

  const processDocument = async (forceOCR = false) => {
    if (!documentId) return;
    
    setProcessing(true);
    try {
      const url = `${API_URL}/api/document-ai/process/${documentId}${forceOCR ? '?force_ocr=true' : ''}`;
      const response = await fetch(url, {
        method: 'POST',
        headers
      });
      
      if (!response.ok) {
        const error = await response.json();
        const d = error?.detail;
        const msg = (d && typeof d === 'object') ? (d.message || d.error || 'Processing failed')
                    : (typeof d === 'string' && d.trim() ? d : (error?.message || 'Processing failed'));
        throw new Error(msg);
      }
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(`Document processed! ${result.stages_completed?.length || 0} stages completed`);
        if (onRefresh) onRefresh();
      } else {
        toast.error(result.error || 'Processing failed');
      }
    } catch (err) {
      console.error('Processing error:', err);
      toast.error(err.message || 'Failed to process document');
    } finally {
      setProcessing(false);
    }
  };

  const hasVisionData = Object.keys(visionAI).length > 0;
  const hasInsightData = Object.keys(aiInsight).length > 0 && aiInsight.status !== 'failed';
  const hasAnyData = hasVisionData || hasInsightData;

  // Summary stats with safe fallbacks
  const extractedTextLength = safeGet(aiInsight, 'extracted_text_length') || 
                              safeGet(aiInsight, 'text_length') || 0;
  const detectedLanguage = safeGet(aiInsight, 'detected_language') || 'en';
  const classifiedAs = safeGet(aiInsight, 'classification', 'category') || documentType;
  const entityCount = Object.keys(safeGet(aiInsight, 'entities', 'entities') || {}).length;
  const hasWorkflowSuggestions = !!safeGet(aiInsight, 'workflow_suggestion', 'ai_suggestions');
  
  // Get summary with fallback
  const summary = safeGet(aiInsight, 'summary') || 
                  (hasAnyData ? 'Document has been processed.' : 'No summary available.');

  const languageNames = {
    en: 'English', hi: 'Hindi', bn: 'Bengali', ta: 'Tamil',
    te: 'Telugu', mr: 'Marathi', gu: 'Gujarati', kn: 'Kannada',
    ml: 'Malayalam', pa: 'Punjabi', or: 'Odia', as: 'Assamese',
    ur: 'Urdu', sa: 'Sanskrit'
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'ocr', label: 'OCR Results', icon: FileText },
    { id: 'entities', label: 'Extracted Data', icon: Brain },
    { id: 'workflow', label: 'Suggestions', icon: Workflow }
  ];

  return (
    <div className="space-y-4" data-testid="document-ai-insights">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                Document AI Insights
              </CardTitle>
              <CardDescription>
                Intelligent document processing results
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {aiProcessedAt && (
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Date(aiProcessedAt).toLocaleString()}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => processDocument(false)}
                disabled={processing}
                data-testid="doc-ai-process-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1" />
                    {hasAnyData ? 'Reprocess' : 'Process with AI'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        
        {/* Quick Stats */}
        {hasAnyData && (
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-2xl font-bold text-slate-900">
                  {extractedTextLength > 0 ? `${(extractedTextLength / 1000).toFixed(1)}K` : '-'}
                </div>
                <div className="text-xs text-slate-500">Characters Extracted</div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                  {languageNames[detectedLanguage] || detectedLanguage}
                  <Languages className="w-4 h-4 text-slate-400" />
                </div>
                <div className="text-xs text-slate-500">Detected Language</div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-2xl font-bold text-slate-900">
                  {classifiedAs || documentType || '-'}
                </div>
                <div className="text-xs text-slate-500">Document Type</div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-2xl font-bold text-slate-900">
                  {entityCount}
                </div>
                <div className="text-xs text-slate-500">Fields Extracted</div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* No Data State */}
      {!hasAnyData && !processing && (
        <Card>
          <CardContent className="py-12 text-center">
            <Sparkles className="w-16 h-16 mx-auto mb-4 text-slate-200" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">
              No AI Analysis Available
            </h3>
            <p className="text-slate-500 mb-4 max-w-md mx-auto">
              Process this document with AI to extract text from images, 
              identify entities, and get workflow suggestions.
            </p>
            <Button onClick={() => processDocument(false)} disabled={processing} data-testid="doc-ai-process-empty-btn">
              <Sparkles className="w-4 h-4 mr-2" />
              Process with Document AI
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Tab Navigation */}
      {hasAnyData && (
        <>
          <div className="flex border-b">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="mt-4">
            {activeTab === 'overview' && (
              <div className="grid md:grid-cols-2 gap-4">
                {/* AI Summary */}
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-amber-500" />
                      AI Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700">{summary}</p>
                  </CardContent>
                </Card>

                {/* Processing Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Processing Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {safeGet(visionAI, 'layout_analysis') && (
                      <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                        <span className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          Layout Analysis
                        </span>
                        <Badge variant="outline">Complete</Badge>
                      </div>
                    )}
                    {(safeGet(visionAI, 'handwriting_ocr') || safeGet(visionAI, 'standard_ocr')) && (
                      <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                        <span className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          Text Extraction (OCR)
                        </span>
                        <Badge variant="outline">
                          {safeGet(visionAI, 'handwriting_ocr') ? 'Handwritten' : 'Standard'}
                        </Badge>
                      </div>
                    )}
                    {safeGet(aiInsight, 'classification') && (
                      <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                        <span className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          Classification
                        </span>
                        <Badge>{safeDisplay(safeGet(aiInsight, 'classification', 'category'), documentType)}</Badge>
                      </div>
                    )}
                    {entityCount > 0 && (
                      <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                        <span className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          Entity Extraction
                        </span>
                        <Badge variant="outline">{entityCount} fields</Badge>
                      </div>
                    )}
                    {aiInsight.status === 'completed' && (
                      <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                        <span className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          AI Indexing
                        </span>
                        <Badge variant="outline">Complete</Badge>
                      </div>
                    )}
                    {aiInsight.status === 'failed' && (
                      <div className="flex items-center justify-between p-2 bg-red-50 rounded">
                        <span className="flex items-center gap-2">
                          <XCircle className="w-4 h-4 text-red-600" />
                          Processing Failed
                        </span>
                        <Badge variant="destructive">Error</Badge>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Layout Info */}
                {safeGet(visionAI, 'layout_analysis', 'analysis') && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Document Structure</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {visionAI.layout_analysis.analysis.layout && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Orientation</span>
                            <span>{visionAI.layout_analysis.analysis.layout.orientation}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Columns</span>
                            <span>{visionAI.layout_analysis.analysis.layout.columns}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Has Tables</span>
                            <span>{visionAI.layout_analysis.analysis.layout.has_tables ? 'Yes' : 'No'}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Has Signatures</span>
                            <span>{visionAI.layout_analysis.analysis.layout.has_signatures ? 'Yes' : 'No'}</span>
                          </div>
                        </>
                      )}
                      {visionAI.layout_analysis.analysis.quality && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Handwritten</span>
                            <Badge variant={visionAI.layout_analysis.analysis.quality.is_handwritten ? 'default' : 'secondary'}>
                              {visionAI.layout_analysis.analysis.quality.is_handwritten ? 'Yes' : 'No'}
                            </Badge>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Readability</span>
                            <span className="capitalize">{visionAI.layout_analysis.analysis.quality.readability}</span>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {activeTab === 'ocr' && (
              <HandwrittenPreviewPanel 
                visionAI={visionAI} 
                onReprocess={() => processDocument(true)}
              />
            )}

            {activeTab === 'entities' && (
              <ExtractedFieldsPanel 
                aiInsight={aiInsight}
                documentType={documentType}
              />
            )}

            {activeTab === 'workflow' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Workflow className="w-5 h-5 text-purple-600" />
                    Workflow Suggestions
                  </CardTitle>
                  <CardDescription>
                    AI-generated workflow recommendations (suggestions only - no automatic actions)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {aiInsight.workflow_suggestion ? (
                    <div className="space-y-4">
                      {/* Template Suggestion */}
                      {aiInsight.workflow_suggestion.template_suggestion && (
                        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                          <h4 className="font-medium text-purple-800 mb-2">
                            Suggested Workflow: {aiInsight.workflow_suggestion.template_suggestion.workflow}
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {aiInsight.workflow_suggestion.template_suggestion.stages?.map((stage, idx) => (
                              <div key={idx} className="flex items-center">
                                <Badge variant="outline" className="bg-white">
                                  {stage.replace(/_/g, ' ')}
                                </Badge>
                                {idx < aiInsight.workflow_suggestion.template_suggestion.stages.length - 1 && (
                                  <ChevronRight className="w-4 h-4 text-slate-400 mx-1" />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* AI Recommendations */}
                      {aiInsight.workflow_suggestion.ai_suggestions?.recommended_next_steps && (
                        <div>
                          <h4 className="font-medium text-slate-700 mb-2">Recommended Actions</h4>
                          <div className="space-y-2">
                            {aiInsight.workflow_suggestion.ai_suggestions.recommended_next_steps.map((step, idx) => (
                              <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                                <div className="font-medium text-slate-800">{step.action}</div>
                                <div className="text-sm text-slate-500">{step.reason}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Entity-based Recommendations */}
                      {aiInsight.workflow_suggestion.entity_based_recommendations?.recommendations?.length > 0 && (
                        <div>
                          <h4 className="font-medium text-slate-700 mb-2">Based on Extracted Data</h4>
                          <div className="space-y-2">
                            {aiInsight.workflow_suggestion.entity_based_recommendations.recommendations.map((rec, idx) => (
                              <div key={idx} className="flex items-start gap-2 p-2 bg-blue-50 rounded">
                                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                                <div>
                                  <span className="font-medium text-blue-800">{rec.action}</span>
                                  <span className="text-sm text-blue-600 block">{rec.reason}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Disclaimer */}
                      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-700">
                        <strong>Note:</strong> These are AI-generated suggestions only. 
                        No automatic actions will be taken. Review and approve before proceeding.
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Workflow className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No workflow suggestions available</p>
                      <p className="text-sm">Process the document to get AI recommendations</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}

      {/* AI Status Footer */}
      {aiStatus && (
        <div className="text-xs text-slate-400 text-center">
          Document AI {aiStatus.enabled ? 'enabled' : 'disabled'} • 
          {aiStatus.supported_languages?.length || 0} languages supported
        </div>
      )}
    </div>
  );
};

export default DocumentAIInsights;
